import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import ZenScoreGauge from "../ZenScoreGauge";
import Sparkline from "../Sparkline";
import AnimatedNumber from "@/components/foundation/AnimatedNumber";

/**
 * DashboardScreen — content rendered inside PhoneMockup showing the ZenMe app dashboard.
 * Non-interactive visual mockup for marketing purposes.
 */
export const DashboardScreen = () => {
  const reduce = useReducedMotion();
  const revenueData = [12, 15, 13, 20, 22, 26, 28, 32, 30, 36, 40, 44];

  return (
    <div
      className="relative flex h-full w-full flex-col overflow-hidden bg-gradient-to-b from-[#0a0f0b] via-[#080808] to-[#050505] px-5 pb-5 pt-14 text-white"
      data-testid="app-screen-dashboard"
    >
      {/* Status bar */}
      <div className="absolute left-0 right-0 top-3 flex items-center justify-between px-8 text-[10px] font-medium text-white/70">
        <span>9:41</span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-white/60" />
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-white/60" />
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-white/60" />
        </span>
      </div>

      {/* Greeting */}
      <div className="flex items-center gap-3 pt-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-emerald/40 to-brand-emerald/10 text-xs font-semibold text-brand-emerald">
          A
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-text-muted">Good morning,</span>
          <span className="text-sm font-semibold leading-tight">Alex</span>
        </div>
      </div>

      {/* ZenScore card */}
      <div className="mt-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-text-muted">
            Your ZenScore
          </span>
          <span className="inline-flex items-center gap-1 text-[9px] text-brand-emerald">
            <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-brand-emerald" />
            LIVE
          </span>
        </div>
        <div className="flex items-center justify-center py-1">
          <ZenScoreGauge
            value={782}
            size={150}
            label="/ 1000"
            compact
            data-testid="app-screen-gauge"
          />
        </div>
        <div className="text-center text-[10px] font-medium text-brand-emerald">
          Strong funding readiness
        </div>
      </div>

      {/* Metrics row */}
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
          <span className="text-[9px] uppercase tracking-widest text-text-muted">
            Available Balance
          </span>
          <div className="mt-1 font-display text-base font-semibold text-white tabular-nums">
            <AnimatedNumber
              value={127450}
              duration={1.4}
              formatter={(n) =>
                `$${Math.round(n).toLocaleString("en-US")}`
              }
            />
          </div>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
          <span className="text-[9px] uppercase tracking-widest text-text-muted">
            Monthly Revenue
          </span>
          <div className="mt-1 flex items-center justify-between gap-2">
            <Sparkline
              data={revenueData}
              width={64}
              height={22}
              stroke="#00E676"
            />
            <span className="text-[10px] font-semibold text-brand-emerald">
              +18%
            </span>
          </div>
        </div>
      </div>

      {/* Funding range */}
      <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center justify-between">
          <span className="text-[9px] uppercase tracking-widest text-text-muted">
            Funding Range
          </span>
          <span className="text-xs font-semibold text-white tabular-nums">
            $45K – $120K
          </span>
        </div>
        <div className="relative mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.05]">
          <motion.div
            initial={reduce ? { width: "78%" } : { width: 0 }}
            animate={{ width: "78%" }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            className="absolute left-0 top-0 h-full rounded-full"
            style={{
              background:
                "linear-gradient(90deg, #00C853 0%, #00E676 100%)",
              boxShadow: "0 0 12px rgba(0,230,118,0.4)",
            }}
          />
        </div>
      </div>

      {/* Bottom nav */}
      <div className="mt-auto flex items-center justify-around rounded-2xl border border-white/[0.06] bg-white/[0.02] px-2 py-2">
        {[
          { label: "Home", active: true },
          { label: "Score", active: false },
          { label: "Offers", active: false },
          { label: "Docs", active: false },
        ].map((tab) => (
          <div
            key={tab.label}
            className={
              tab.active
                ? "flex flex-col items-center gap-0.5 text-brand-emerald"
                : "flex flex-col items-center gap-0.5 text-text-muted"
            }
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            <span className="text-[9px] font-medium">{tab.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardScreen;
